'use client'

import { useRouter } from '@/lib/navigation/PageNavigation.client'
import { usePathname, useSearchParams } from 'next/navigation'
import { useCallback, useMemo } from 'react'

type URLParam = {
  name: string
  value: string
}

export function useURLParams() {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  /**
   * Add or update URL parameters from the current URL.
   *
   * @param urlParams - Array of URL parameters to add or update.
   * @param showPageLoader - Whether to show the page loader.
   */
  const setURLParams = useCallback(
    (urlParams: URLParam[]) => {
      const params = new URLSearchParams(searchParams.toString())

      urlParams.forEach(({ name, value }) => {
        params.set(name, value)
      })

      router.replace(`${pathname}?${params}`)
    },
    [pathname, router, searchParams],
  )

  /**
   * Remove URL parameters from the current URL.
   *
   * @param urlParams - Array of URL parameters to remove.
   * @param showPageLoader - Whether to show the page loader.
   */
  const removeURLParams = useCallback(
    (urlParams: StrictOmit<URLParam, 'value'>[]) => {
      const params = new URLSearchParams(searchParams.toString())

      urlParams.forEach(({ name }) => {
        params.delete(name)
      })

      router.push(`${pathname}?${params}`)
    },
    [pathname, router, searchParams],
  )

  return useMemo(() => ({ setURLParams, removeURLParams }), [setURLParams, removeURLParams])
}
